<template>
  <svg :viewBox="`0 0 ${SIZE} ${SIZE}`" :width="size" :height="size" class="text-zinc-800 dark:text-zinc-100" role="img"
    :aria-label="`Turning movement diagram, ${formatNumber(summary.total)} total`">
    <!-- road legs (laid out downwards for the south leg and rotated into place) -->
    <template v-for="leg in legs" :key="leg.direction">
      <rect :x="-ROAD_HALF" :y="ROAD_HALF" :width="ROAD_HALF * 2" :height="SIZE / 2 - ROAD_HALF"
        :transform="`translate(${C} ${C}) rotate(${leg.angle})`" class="fill-zinc-300 dark:fill-zinc-700"
        :opacity="leg.active ? 1 : 0.3" />
    </template>
    <rect :x="C - ROAD_HALF" :y="C - ROAD_HALF" :width="ROAD_HALF * 2" :height="ROAD_HALF * 2"
      class="fill-zinc-300 dark:fill-zinc-700" />
    <!-- movements -->
    <template v-for="leg in legs" :key="`moves-${leg.direction}`">
      <template v-if="leg.active">
        <g v-for="item in leg.items" :key="item.turn">
          <g :transform="`translate(${item.glyph.x} ${item.glyph.y}) rotate(${leg.angle})`" class="stroke-accent"
            fill="none" stroke-width="2" stroke-linecap="round" stroke-linejoin="round" :opacity="item.count > 0 ? 1 : 0.35">
            <path :d="GLYPHS[item.turn]" />
          </g>
          <text :x="item.label.x" :y="item.label.y" text-anchor="middle" dominant-baseline="middle" font-size="9"
            :font-weight="item.count > 0 ? 600 : 400" fill="currentColor" :opacity="item.count > 0 ? 1 : 0.5">
            {{ formatNumber(item.count) }}
          </text>
        </g>
      </template>
      <text :x="leg.letter.x" :y="leg.letter.y" text-anchor="middle" dominant-baseline="middle" font-size="12"
        font-weight="700" fill="currentColor" :opacity="leg.active ? 1 : 0.4">{{ leg.letter.text }}</text>
    </template>
    <!-- intersection total -->
    <text :x="C" :y="C - 4" text-anchor="middle" dominant-baseline="middle" font-size="10" fill="currentColor"
      opacity="0.7">Total</text>
    <text :x="C" :y="C + 9" text-anchor="middle" dominant-baseline="middle" font-size="11" font-weight="700"
      fill="currentColor">{{ formatNumber(summary.total) }}</text>
  </svg>
</template>

<script setup>
import { computed } from "vue";
import { DIRECTIONS } from "@/lib/intersections/movements";

const props = defineProps({
  /** { total, approaches: { north: { total, left, through, right, uturn }, ... } } */
  summary: {
    type: Object,
    required: true
  },
  size: {
    type: Number,
    default: 280
  }
});

const SIZE = 320;
const C = SIZE / 2;
const ROAD_HALF = 48;
// radial distances from the centre, measured along the approach leg
const GLYPH_RADIUS = 74;
const LABEL_RADIUS = 104;
const LETTER_RADIUS = 148;
// lateral spacing of the left / through / right items across the leg (wide enough for 5 digit counts)
const LANE_OFFSET = 32;

// rotation that moves the south (bottom) leg layout to each approach
const ANGLES = { south: 0, west: 90, north: 180, east: 270 };
const LETTERS = { north: "N", east: "E", south: "S", west: "W" };
// drawn for a vehicle heading up (north) and rotated with the leg
const GLYPHS = {
  left: "M0 11 L0 -1 Q0 -8 -6 -8 L-10 -8 M-6 -12 L-10 -8 L-6 -4",
  through: "M0 11 L0 -10 M-4 -6 L0 -10 L4 -6",
  right: "M0 11 L0 -1 Q0 -8 6 -8 L10 -8 M6 -12 L10 -8 L6 -4"
};
const TURNS = ["left", "through", "right"];

const formatNumber = (value) => (Number.isFinite(value) ? value.toLocaleString() : "—");

/** Rotate a point laid out for the south leg (x lateral, y radial downwards) onto a leg. */
function place(x, y, angle) {
  const radians = (angle * Math.PI) / 180;
  return {
    x: C + x * Math.cos(radians) - y * Math.sin(radians),
    y: C + x * Math.sin(radians) + y * Math.cos(radians)
  };
}

const legs = computed(() => DIRECTIONS.map((direction) => {
  const angle = ANGLES[direction];
  const approach = props.summary.approaches[direction];
  // driver's left is on the -x side when heading up the page
  const items = TURNS.map((turn, index) => {
    const lateral = (index - 1) * LANE_OFFSET;
    return {
      turn,
      count: approach ? approach[turn] : 0,
      glyph: place(lateral, GLYPH_RADIUS, angle),
      label: place(lateral, LABEL_RADIUS, angle)
    };
  });
  return {
    direction,
    angle,
    active: Boolean(approach),
    approach,
    items,
    letter: { ...place(0, LETTER_RADIUS, angle), text: LETTERS[direction] }
  };
}));
</script>
